import { Plus, Unlink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ChannelBrandProfileInput, ChannelRecord } from '@nadoutong/contracts';
import { useUpdateChannel } from '../api/useChannels.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';
import {
  CHANNEL_BRANDS,
  brandLabel,
  enabledBrandProfiles,
  profileForBrand,
  type ChannelBrandId,
} from '../lib/channelBrands.ts';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  Drawer,
  FormField,
  IconButton,
  Input,
  Textarea,
} from '../ui/index.ts';

interface Props {
  readonly channel: ChannelRecord | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

function normalizeProfiles(
  profiles: readonly ChannelBrandProfileInput[],
): ChannelBrandProfileInput[] {
  return CHANNEL_BRANDS.flatMap(({ id }) => {
    const profile = profiles.find((entry) => entry.brand === id);
    if (profile === undefined || profile.enabled === false) return [];
    return [{
      brand: id,
      public_slug: profile.public_slug ?? null,
      display_name: profile.display_name ?? null,
      description: profile.description ?? null,
      enabled: true,
    }];
  });
}

function serializeProfiles(
  profiles: readonly ChannelBrandProfileInput[],
): ChannelBrandProfileInput[] {
  return normalizeProfiles(profiles).map((profile) => {
    const publicSlug = profile.public_slug?.trim() ?? '';
    const displayName = profile.display_name?.trim() ?? '';
    const description = profile.description?.trim() ?? '';
    return {
      brand: profile.brand,
      public_slug: publicSlug.length > 0 ? publicSlug : null,
      display_name: displayName.length > 0 ? displayName : null,
      description: description.length > 0 ? description : null,
      enabled: true,
    };
  });
}

export function ChannelBrandBindingDrawer({
  channel,
  isOpen,
  onClose,
}: Props): JSX.Element | null {
  const t = useT();
  const toast = useToast();
  const updateChannelMut = useUpdateChannel();
  const [profiles, setProfiles] = useState<ChannelBrandProfileInput[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || channel === null) return;
    setProfiles(
      enabledBrandProfiles(channel).map((profile) => ({
        brand: profile.brand,
        public_slug: profile.public_slug,
        display_name: profile.display_name,
        description: profile.description,
        enabled: true,
      })),
    );
    setError(null);
  }, [channel, isOpen]);

  const boundProfiles = useMemo(() => normalizeProfiles(profiles), [profiles]);
  const boundBrands = useMemo(
    () => new Set(boundProfiles.map((profile) => profile.brand)),
    [boundProfiles],
  );
  const unboundBrands = CHANNEL_BRANDS.filter(({ id }) => !boundBrands.has(id));

  if (channel === null) return null;
  const activeChannel = channel;

  function updateProfile(
    brand: ChannelBrandId,
    patch: Partial<Pick<ChannelBrandProfileInput, 'public_slug' | 'display_name' | 'description'>>,
  ): void {
    setProfiles((current) =>
      normalizeProfiles(current).map((profile) =>
        profile.brand === brand ? { ...profile, ...patch } : profile,
      ),
    );
  }

  function addBrand(brand: ChannelBrandId): void {
    setProfiles((current) => normalizeProfiles([
      ...current,
      { brand, public_slug: null, display_name: null, description: null, enabled: true },
    ]));
  }

  function removeBrand(brand: ChannelBrandId): void {
    setProfiles((current) =>
      normalizeProfiles(current).filter((profile) => profile.brand !== brand),
    );
  }

  function handleSave(): void {
    const body = { brand_profiles: serializeProfiles(profiles) };
    setError(null);
    updateChannelMut.mutate(
      { id: activeChannel.id, body },
      {
        onSuccess: () => {
          toast.success(t('channels.brand_bindings.toast_saved'));
          onClose();
        },
        onError: (err) => {
          const message = coerceErrorMessage(err);
          setError(message);
          toast.error(message);
        },
      },
    );
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={activeChannel.display_name}
      description={
        <span className="flex flex-wrap items-center gap-2">
          <span>{t('channels.brand_bindings.eyebrow')}</span>
          <Chip tone="neutral" lamp={false} className="font-mono">
            {activeChannel.vendor}
          </Chip>
          <span className="font-mono text-paper-low">{activeChannel.slug}</span>
          {activeChannel.supplier_name ? <span>{activeChannel.supplier_name}</span> : null}
        </span>
      }
      width="lg"
      closeLabel={t('channels.brand_bindings.close')}
      testId="channel-brand-drawer"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('channels.create_modal.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={updateChannelMut.isPending}
            data-testid="channel-brand-save"
          >
            {updateChannelMut.isPending
              ? t('common.saving')
              : t('channels.brand_bindings.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="m-0 text-label text-paper-mid">
              {t('channels.brand_bindings.bound_title')}
            </h4>
            <span className="font-mono text-label text-paper-low">{boundProfiles.length}</span>
          </div>

          <div className="flex flex-col gap-2" data-testid="channel-brand-bound-list">
            {boundProfiles.length === 0 ? (
              <div className="rounded-lg border border-line-soft bg-well px-4 py-6 text-center text-body text-paper-low">
                {t('channels.brand_bindings.empty_bound')}
              </div>
            ) : (
              boundProfiles.map((profile) => {
                const brand = profile.brand as ChannelBrandId;
                const existing = profileForBrand(activeChannel, brand);
                return (
                  <div key={brand} className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-body font-medium text-paper">{brandLabel(brand)}</div>
                        {existing?.public_slug ? (
                          <div className="mt-0.5 font-mono text-label text-signal">{existing.public_slug}</div>
                        ) : (
                          <div className="mt-0.5 font-mono text-label text-paper-low">{activeChannel.slug}</div>
                        )}
                        {activeChannel.description != null ? (
                          <div className="mt-1 text-label text-paper-low">
                            {t('channels.brand_profiles.description_fallback')}
                          </div>
                        ) : null}
                      </div>
                      <IconButton
                        size="sm"
                        label={t('channels.brand_bindings.remove_brand', { brand: brandLabel(brand) })}
                        onClick={() => removeBrand(brand)}
                        data-testid={`channel-brand-remove-${brand}`}
                        className="text-bad hover:bg-bad-dim hover:text-bad"
                      >
                        <Unlink aria-hidden="true" className="h-4 w-4" />
                      </IconButton>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <FormField
                        label={t('channels.brand_profiles.public_slug')}
                        fieldId={`channel-brand-public-slug-${brand}`}
                      >
                        <Input
                          id={`channel-brand-public-slug-${brand}`}
                          mono
                          value={profile.public_slug ?? ''}
                          onChange={(event) => updateProfile(brand, { public_slug: event.currentTarget.value })}
                          placeholder={activeChannel.slug}
                          data-testid={`channel-brand-public-slug-${brand}`}
                        />
                      </FormField>
                      <FormField
                        label={t('channels.brand_profiles.display_name')}
                        fieldId={`channel-brand-display-name-${brand}`}
                      >
                        <Input
                          id={`channel-brand-display-name-${brand}`}
                          value={profile.display_name ?? ''}
                          onChange={(event) => updateProfile(brand, { display_name: event.currentTarget.value })}
                          placeholder={activeChannel.display_name}
                          data-testid={`channel-brand-display-name-${brand}`}
                        />
                      </FormField>
                    </div>
                    <FormField
                      label={t('channels.brand_profiles.description')}
                      fieldId={`channel-brand-description-${brand}`}
                    >
                      <Textarea
                        id={`channel-brand-description-${brand}`}
                        value={profile.description ?? ''}
                        onChange={(event) => updateProfile(brand, { description: event.currentTarget.value })}
                        placeholder={activeChannel.description ?? t('channels.brand_profiles.description_placeholder')}
                        rows={3}
                        className="resize-none"
                        data-testid={`channel-brand-description-${brand}`}
                      />
                    </FormField>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h4 className="m-0 text-label text-paper-mid">
            {t('channels.brand_bindings.add_title')}
          </h4>
          <div className="flex flex-wrap gap-2" data-testid="channel-brand-add-list">
            {unboundBrands.length === 0 ? (
              <span className="text-body text-paper-low">{t('channels.brand_bindings.no_unbound')}</span>
            ) : (
              unboundBrands.map(({ id, label }) => (
                <Button
                  key={id}
                  variant="secondary"
                  size="sm"
                  onClick={() => addBrand(id)}
                  data-testid={`channel-brand-add-${id}`}
                >
                  <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                  {label}
                </Button>
              ))
            )}
          </div>
        </section>

        {error !== null ? (
          <div role="alert" className="rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright">
            {error}
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
